<template>
  <div class="purchase-by-date-report">
    <v-card flat class="report-page-card">
      <ReportViewBar
        title="Purchase By Date"
        :start="start"
        :end="end"
        :export-data="exportData"
        :export-fields="exportFields"
      />

      <v-card-text class="report-content">
        <v-card flat outlined class="report-table-card">
          <v-card-title class="report-table-header">
            <div>
              <div class="report-title">Purchase Details</div>
              <div class="report-subtitle">
                {{ Purchase.length }} purchases found
              </div>
            </div>

            <v-spacer />

            <div class="summary-box">
              <div class="summary-label">Total</div>
              <div class="summary-value">{{ total | currency }}</div>
            </div>

            <div class="summary-box">
              <div class="summary-label">Balance Due</div>
              <div class="summary-value due">{{ due | currency }}</div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-0">
            <v-progress-linear v-if="isLoading" indeterminate height="3" />

            <v-data-table
              v-else
              dense
              readonly
              :sort-by="sortBy"
              :mobile-breakpoint="0"
              :headers="headers"
              :items="Purchase"
              :items-per-page="-1"
              hide-default-footer
              class="purchase-table"
            >
              <template v-slot:item.date="{ item }">
                {{ item.date | moment("MM/DD/YYYY") }}
              </template>

              <template v-slot:item.localId="{ item }">
                <a
                  href="#"
                  class="purchase-link"
                  @click.prevent="viewPurchase(item)"
                >
                  {{ item.localId }}
                </a>
              </template>

              <template v-slot:item.vendorName="{ item }">
                <span class="text-capitalize">
                  {{ getVendorName(item) }}
                </span>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  v-if="Number(item.due || 0) === 0"
                  small
                  outlined
                  color="success"
                >
                  Paid
                </v-chip>

                <v-chip
                  v-else
                  small
                  outlined
                  color="warning"
                >
                  Due
                </v-chip>
              </template>

              <template v-slot:item.total="{ item }">
                <span class="money">
                  {{ item.total | currency }}
                </span>
              </template>

              <template v-slot:item.due="{ item }">
                <span class="money due">
                  {{ item.due | currency }}
                </span>
              </template>

              <template v-slot:body.append v-if="Purchase.length > 0">
                <tr class="total-row">
                  <td colspan="4" class="text-right font-weight-bold">
                    Total
                  </td>

                  <td class="text-right font-weight-bold">
                    {{ total | currency }}
                  </td>

                  <td class="text-right font-weight-bold">
                    {{ due | currency }}
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="empty-state">
                  No purchases found for this date range.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import PurchaseService from "@/modules/Inventory/Purchase/service";

export default {
  name: "purchase-by-date-report",

  components: {
    ReportViewBar,
  },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Purchase: [],
      sortBy: "date",
      exportFields: {
        Date: "date",
        "Purchase #": "localId",
        Vendor: "vendorName",
        Status: "status",
        Total: "total",
        "Balance Due": "due",
      },
      headers: [
        {
          text: "Date",
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Purchase #",
          align: "left",
          value: "localId",
          sortable: true,
        },
        {
          text: "Vendor",
          align: "left",
          value: "vendorName",
          sortable: true,
        },
        {
          text: "Status",
          align: "left",
          value: "status",
          sortable: true,
        },
        {
          text: "Total",
          align: "right",
          value: "total",
          sortable: true,
        },
        {
          text: "Balance Due",
          align: "right",
          value: "due",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    total() {
      return this.Purchase.reduce(
        (sum, row) => sum + Number(row.total || 0),
        0
      );
    },

    due() {
      return this.Purchase.reduce(
        (sum, row) => sum + Number(row.due || 0),
        0
      );
    },

    exportData() {
      return this.Purchase.map((row) => ({
        date: row.date,
        localId: row.localId,
        vendorName: this.getVendorName(row),
        status: Number(row.due || 0) === 0 ? "Paid" : "Due",
        total: Number(row.total || 0),
        due: Number(row.due || 0),
      }));
    },
  },

  async created() {
    this.start = this.$route.query.start || null;
    this.end = this.$route.query.end || null;

    await this.getAll();
  },

  methods: {
    getVendorName(purchase) {
      if (purchase.Vendor && purchase.Vendor.name) return purchase.Vendor.name;
      return "N/A";
    },

    viewPurchase(purchase) {
      const routeData = this.$router.resolve({
        path: `/global/purchase/${purchase.id}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850,height=1100");
    },

    async getAll() {
      const where = {
        start: this.start,
        end: this.end,
        limit: 2500,
      };

      try {
        const response = await PurchaseService.getAll(where);
        this.Purchase = response.data || [];
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.purchase-by-date-report {
  background: #f5f7fb;
  min-height: 100vh;
  padding: 16px;
}

.report-page-card {
  border-radius: 18px;
  overflow: hidden;
}

.report-content {
  padding: 20px;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.summary-value.due,
.money.due {
  color: #b91c1c;
}

.purchase-link {
  font-weight: 700;
  text-decoration: none;
}

.money {
  font-weight: 700;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.purchase-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.purchase-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .purchase-by-date-report {
    background: #fff;
    padding: 0;
  }

  .report-page-card,
  .report-table-card {
    box-shadow: none !important;
    border-radius: 0;
  }

  .report-content {
    padding: 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>